import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Header2 from '../Header2';
import Footer from '../Footer';
import { fetchMovies, mapMovieForCard } from '../../api/starviewApi';
import { useCountry } from '../../context/CountryContext';
import './CountryExclusive.css';

const DESKTOP_BREAKPOINT = 900;

export default function CountryExclusive() {
  const { countries } = useCountry();
  const [isDesktop, setIsDesktop] = useState(false);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // No country filter - the whole real catalog, so exclusivity can be
    // computed across every theater at once.
    fetchMovies({ size: 100, country: '' })
      .then((raw) => { if (!cancelled) setMovies(raw.map(mapMovieForCard)); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // A title is "exclusive" to a country if every real listing of that title
  // across the whole catalog belongs to that one country - computed live
  // from real fetched data, not a hand-maintained flag.
  const exclusivesByCountry = useMemo(() => {
    const countriesByTitle = new Map();
    movies.forEach((m) => {
      if (!countriesByTitle.has(m.title)) countriesByTitle.set(m.title, new Set());
      countriesByTitle.get(m.title).add(m.country);
    });
    const grouped = {};
    movies.forEach((m) => {
      const countrySet = countriesByTitle.get(m.title);
      if (countrySet && countrySet.size === 1) {
        if (!grouped[m.country]) grouped[m.country] = [];
        if (!grouped[m.country].some((existing) => existing.title === m.title)) {
          grouped[m.country].push(m);
        }
      }
    });
    return grouped;
  }, [movies]);

  const countryInfo = (code) => countries.find((c) => c.code === code);
  const countryCodesWithExclusives = Object.keys(exclusivesByCountry).sort(
    (a, b) => exclusivesByCountry[b].length - exclusivesByCountry[a].length
  );

  const rowRefs = useRef({});
  const scrollRow = (code, direction) => {
    const row = rowRefs.current[code];
    if (!row) return;
    const cardWidth = row.firstElementChild?.getBoundingClientRect().width || 160;
    row.scrollBy({ left: direction * (cardWidth + 18) * 2, behavior: 'smooth' });
  };

  return (
    <div className="ce-root">
      <Header />
      {isDesktop && <Header2 />}

      <section className="ce-hero">
        <span className="ce-eyebrow">Around the World</span>
        <h1 className="ce-title">Country Exclusive Films</h1>
        <p className="ce-subtitle">Titles currently playing at only one StarView Cinema territory — real availability, computed live from what's actually showing right now.</p>
      </section>

      <section className="ce-content">
        {loading && <p className="ce-status">Loading catalog…</p>}
        {!loading && error && <p className="ce-status">{error}</p>}
        {!loading && !error && countryCodesWithExclusives.length === 0 && (
          <p className="ce-status">Every film currently playing is available in more than one territory.</p>
        )}
        {!loading && !error && countryCodesWithExclusives.map((code) => (
          <div key={code} className="ce-country-block">
            <div className="ce-country-heading">
              <span className="ce-flag">{countryInfo(code)?.flag || '🌐'}</span>
              <h2>{countryInfo(code)?.label || code}</h2>
              <span className="ce-count">{exclusivesByCountry[code].length} exclusive</span>
            </div>
            <div className="ce-movie-row-wrap">
              <button className="ce-arrow-overlay left" onClick={() => scrollRow(code, -1)} aria-label="Scroll left">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L8 12L15 19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="ce-movie-row" ref={(el) => { rowRefs.current[code] = el; }}>
                {exclusivesByCountry[code].map((movie) => (
                  <Link key={movie.id} to={`/Showtimes?movieId=${encodeURIComponent(movie.id)}`} className="ce-movie-card">
                    {movie.image_url ? (
                      <img src={movie.image_url} alt={movie.title} loading="lazy" />
                    ) : (
                      <div className="ce-movie-fallback" />
                    )}
                    <span className="ce-movie-title">{movie.title}</span>
                  </Link>
                ))}
              </div>
              <button className="ce-arrow-overlay right" onClick={() => scrollRow(code, 1)} aria-label="Scroll right">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
}
